<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import countries from 'shared/constants/countries';
import SalesPropertyReferenceField from './SalesPropertyReferenceField.vue';
import {
  formatSalesPartyPhone,
  normalizeSalesPartyCountryCode,
  salesPartyPhoneMaxLength,
} from '../composables/salesPartyPhone';

const props = defineProps({
  countryCode: { type: [String, Number], default: '' },
  phone: { type: [String, Number], default: '' },
  label: { type: String, default: '' },
  error: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const countryCode = computed(() =>
  normalizeSalesPartyCountryCode(props.countryCode)
);
const phone = computed(() =>
  formatSalesPartyPhone(props.phone, countryCode.value)
);
const filled = computed(() => Boolean(String(phone.value).trim()));
const maxLength = computed(() => salesPartyPhoneMaxLength(countryCode.value));
const countryOptions = [
  countries.find(country => country.id === 'BR'),
  ...countries.filter(country => country.id !== 'BR'),
].filter(Boolean);

const updateCountry = value => {
  const code = normalizeSalesPartyCountryCode(value);
  emit('update', 'country_code', code);
  emit('update', 'phone', formatSalesPartyPhone(props.phone, code));
};
const updatePhone = value => {
  if (!props.countryCode) emit('update', 'country_code', countryCode.value);
  emit('update', 'phone', formatSalesPartyPhone(value, countryCode.value));
};
</script>

<template>
  <SalesPropertyReferenceField
    :filled="filled"
    :disabled="disabled"
    :label="label || t('SALES_PARTIES.FORM.PHONE')"
    :error="error"
    required
    :show-success="false"
    test-id="sales-party-phone"
  >
    <div
      class="flex h-8 w-full items-center border-b"
      :class="
        error
          ? 'border-[#D64545]'
          : filled && !disabled
            ? 'border-[#009F94]'
            : 'border-[#A7B7BE]'
      "
    >
      <select
        :value="countryCode"
        :aria-label="t('SALES_PARTIES.FORM.COUNTRY_CODE')"
        :disabled="disabled"
        class="h-full w-[92px] shrink-0 border-0 bg-transparent px-0 text-sm text-[#009F94] outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-[#7F8C93]"
        @change="updateCountry($event.target.value)"
      >
        <option
          v-for="country in countryOptions"
          :key="country.id"
          :value="country.dial_code.slice(1)"
        >
          {{ country.emoji }} {{ country.dial_code }}
        </option>
      </select>
      <input
        :value="phone"
        type="tel"
        inputmode="numeric"
        :maxlength="maxLength"
        :placeholder="t('SALES_PARTIES.FORM.PHONE_PLACEHOLDER')"
        :disabled="disabled"
        class="reset-base h-full min-w-0 flex-1 border-0 bg-transparent px-0 text-base outline-none placeholder:text-[#A7B7BE] focus:ring-0 disabled:cursor-not-allowed disabled:text-[#7F8C93]"
        :class="
          filled && !disabled ? 'font-bold text-[#009F94]' : 'text-[#7F8C93]'
        "
        @input="updatePhone($event.target.value)"
      />
    </div>
  </SalesPropertyReferenceField>
</template>
